import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import type { Role } from '../../../shared/entities/Access';

type FiltersContextValue = {
  roles: Role[];
  search: string;
  setRoles: (roles: Role[]) => void;
  setSearch: (search: string) => void;
  resetFilters: () => void;
};

const noop = () => {
  // do nothing
};

const defaultFiltersContextValue: FiltersContextValue = {
  roles: [],
  search: '',
  setRoles: noop,
  setSearch: noop,
  resetFilters: noop,
};

const FiltersContext = createContext<FiltersContextValue>(
  defaultFiltersContextValue,
);

export function useFilters() {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
}

export function FiltersProvider({ children }: PropsWithChildren) {
  const [roles, setRoles] = useState<Role[]>(defaultFiltersContextValue.roles);
  const [search, setSearch] = useState(defaultFiltersContextValue.search);

  const resetFilters = useCallback(() => {
    setRoles(defaultFiltersContextValue.roles);
    setSearch(defaultFiltersContextValue.search);
  }, []);

  const filters = useMemo(
    () => ({
      roles,
      search,
      setRoles,
      setSearch,
      resetFilters,
    }),
    [roles, search, resetFilters],
  );

  return (
    <FiltersContext.Provider value={filters}>
      {children}
    </FiltersContext.Provider>
  );
}
